import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useLocation } from "react-router-dom";
import api from "../services/api";
import PurchaseCreditsModal from "./PurchaseCreditsModal";

function formatOfferPrice(amount, currency = "usd") {
  const value = Number(amount);
  if (!Number.isFinite(value)) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const SubscriptionStatus = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [paymentType, setPaymentType] = useState(null); // 'subscription' | 'one_time' | null
  const [verifying, setVerifying] = useState(false);
  const [syncingCredits, setSyncingCredits] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [offerPriceLabel, setOfferPriceLabel] = useState(null);

  // Check if we're on the upgrade page
  const isOnUpgradePage = location.pathname.includes("/account/upgrade");

  // Helper function to get the appropriate Host header based on current URL
  const getHostHeader = () => {
    const currentHost = window.location.hostname;
    let hostHeader = currentHost || "app.discoseo.com";

    if (
      currentHost === "localhost" ||
      currentHost.includes("discoseo")
    ) {
      hostHeader = "app.discoseo.com";
    }

    return hostHeader;
  };

  useEffect(() => {
    // Always try to detect payment type from domain configuration first
    fetchDomainConfig();

    if (user) {
      fetchSubscriptionStatus();
    }

    // Check for payment success in URL and auto-verify
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("payment_success") === "true") {
      setTimeout(() => {
        verifyPayment();
        // Clean URL
        window.history.replaceState({}, "", window.location.pathname);
      }, 1000); // Small delay to allow other state to load
    }
  }, [user]);

  useEffect(() => {
    if (paymentType !== "one_time") return undefined;
    let cancelled = false;

    const loadOfferPrice = async () => {
      try {
        const response = await api.call("/subscription/tiers", {
          method: "GET",
          headers: { Host: getHostHeader() },
        });
        const tiers = response.data || [];
        const paid = tiers.find(
          (item) => item.priceId && item.interval === "one_time",
        );
        const label = formatOfferPrice(paid?.price, paid?.currency);
        if (!cancelled && label) setOfferPriceLabel(label);
      } catch (err) {
        console.warn("Failed to load the report price", err);
      }
    };

    loadOfferPrice();
    return () => {
      cancelled = true;
    };
  }, [paymentType]);

  const fetchDomainConfig = async () => {
    try {
      const response = await api.call("/config/branding", {
        method: "GET",
        headers: {
          Host: getHostHeader(),
        },
      });

      if (response.success) {
        // For now, we'll determine payment type based on domain in the response
        const domain = response.data.domain;
        if (domain === "discoseo.com") {
          setPaymentType("one_time");
        } else {
          setPaymentType("subscription");
        }
        console.log("Payment type detected from domain config", {
          domain,
          paymentType: domain === "discoseo.com" ? "one_time" : "subscription",
          host: getHostHeader(),
        });
      }
    } catch (error) {
      // Fallback to subscription if domain config fails
      console.warn(
        "Failed to fetch domain config, defaulting to subscription",
        error,
      );
      setPaymentType("subscription");
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors

      const response = await api.call("/subscription/status", {
        method: "GET",
        headers: {
          Host: getHostHeader(),
        },
      });

      // The api.call method returns the parsed JSON directly
      if (response.success) {
        const subscriptionData = response.data.subscription;
        const usageData = response.data.usage;

        // Detect payment type from response - this comes from domain configuration
        const detectedPaymentType =
          response.data.subscription?.payment_type ||
          subscriptionData?.payment_type;
        setPaymentType(detectedPaymentType || "subscription");
        setSubscription(subscriptionData);
        setUsage(usageData);

        // Check for sync warning
        if (subscriptionData?.syncWarning) {
          console.warn("Subscription sync issue:", subscriptionData.syncError);
        }
      } else {
        console.error("Subscription fetch failed:", response);
        setError(response.error || "Failed to fetch subscription");
      }
    } catch (err) {
      setError("Failed to fetch subscription status");
      console.error("Subscription status error:", err);
    } finally {
      setLoading(false);
    }
  };

  const syncWithStripe = async () => {
    try {
      setSyncing(true);
      setError(null);

      // Call the sync endpoint
      const response = await api.call("/subscription/sync", {
        method: "POST",
        headers: {
          Host: getHostHeader(),
        },
      });

      if (response.success) {
        // Refresh subscription data after successful sync
        await fetchSubscriptionStatus();
      } else {
        setError(response.error || "Failed to sync with Stripe");
      }
    } catch (err) {
      setError("Failed to sync subscription data");
      console.error("Subscription sync error:", err);
    } finally {
      setSyncing(false);
    }
  };

  const verifyPayment = async () => {
    try {
      setVerifying(true);
      setError(null);

      console.log("Verifying payment for one-time payment domain");

      // Call the verify-payment endpoint
      const response = await api.call("/subscription/verify-payment", {
        method: "POST",
        headers: {
          Host: getHostHeader(),
        },
      });

      if (response.success) {
        // Refresh subscription data after successful verification
        await fetchSubscriptionStatus();

        const credits = Number.parseInt(response.credits, 10);
        const successMessage =
          Number.isFinite(credits) && credits > 0
            ? `Payment complete. ${credits} credit${credits === 1 ? "" : "s"} added.`
            : "Payment complete. Your credits are ready.";

        console.log(successMessage);
        alert(successMessage);
      } else {
        setError(response.error || "Failed to verify payment");
      }
    } catch (err) {
      setError("Failed to verify payment");
      console.error("Payment verification error:", err);
    } finally {
      setVerifying(false);
    }
  };

  const syncCredits = async () => {
    try {
      setSyncingCredits(true);
      setError(null);

      console.log("Syncing credits for one-time payment domain");

      // Call the sync-credits endpoint
      const response = await api.call("/subscription/sync-credits", {
        method: "POST",
        headers: {
          Host: getHostHeader(),
        },
      });

      if (response.success) {
        // Refresh subscription data after successful sync
        await fetchSubscriptionStatus();

        // Show success message if credits were added
        if (
          response.synced &&
          response.totalCredits > response.previousCredits
        ) {
          const creditsAdded = response.totalCredits - response.previousCredits;
          const successMessage = `Credits synced! ${creditsAdded} credit${creditsAdded !== 1 ? "s" : ""} added. You have ${response.availableCredits} credit${response.availableCredits !== 1 ? "s" : ""} available.`;
          console.log(successMessage);
          alert(successMessage);
        } else {
          console.log("Credits already in sync:", response);
        }
      } else {
        setError(response.error || "Failed to sync credits");
      }
    } catch (err) {
      setError("Failed to sync credits");
      console.error("Credit sync error:", err);
    } finally {
      setSyncingCredits(false);
    }
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case "tier4":
        return "bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 border border-purple-200";
      case "tier3":
        return "bg-purple-100 text-purple-800";
      case "tier2":
        return "bg-blue-100 text-blue-800";
      case "tier1":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTierName = (tier) => {
    // For one-time payments, use tier names from domain config if available
    if (paymentType === "one_time") {
      switch (tier) {
        case "single_report":
          return "SEO Report";
        case "premium_report":
          return "Premium SEO Report";
        default:
          return tier || "Free";
      }
    }

    // For subscriptions, use original tier names
    switch (tier) {
      case "tier4":
        return "Power";
      case "tier3":
        return "Business";
      case "tier2":
        return "Professional";
      case "tier1":
        return "Basic";
      default:
        return "Free";
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "trialing":
        return "bg-blue-100 text-blue-800";
      case "past_due":
        return "bg-yellow-100 text-yellow-800";
      case "canceled":
      case "unpaid":
        return "bg-red-100 text-red-800";
      case "expired":
      case "consumed":
        return "bg-orange-100 text-orange-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Helper to get display text for payment type
  const getPaymentTypeDisplay = () => {
    return paymentType === "one_time" ? "One-time Payment" : "Subscription";
  };

  if (loading || paymentType === null) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
          <div className="h-8 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (paymentType === "one_time") {
    const credits = subscription?.availableCredits ?? 0;
    const needsCredits = credits <= 0;
    const offerLine = offerPriceLabel
      ? `One site. One report. ${offerPriceLabel} once.`
      : "One site. One report.";
    const buyLabel = offerPriceLabel
      ? `Add Credits — ${offerPriceLabel}`
      : "Add Credits";
    return (
      <>
        <div className="overflow-hidden rounded-2xl border border-border bg-card">
          {needsCredits ? (
            <button
              type="button"
              onClick={() => setShowPurchaseModal(true)}
              className="flex w-full flex-col items-center justify-center gap-4 bg-background/80 px-6 py-10 text-center"
            >
              <span className="text-base text-foreground">{offerLine}</span>
              <span className="rounded-full bg-blue-600 px-6 py-3 text-sm font-medium text-white shadow-sm">
                {buyLabel}
              </span>
            </button>
          ) : (
            <div className="px-6 py-10 text-center">
              <p className="text-base text-foreground">
                {`${credits} credit${credits === 1 ? "" : "s"} ready.`}
              </p>
            </div>
          )}
          {!needsCredits && !isOnUpgradePage && (
            <div className="border-t border-border px-6 py-3 text-center">
              <button
                type="button"
                onClick={() => setShowPurchaseModal(true)}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                Add credits
              </button>
            </div>
          )}
        </div>
        <PurchaseCreditsModal
          isOpen={showPurchaseModal}
          onClose={() => setShowPurchaseModal(false)}
        />
      </>
    );
  }

  const usagePercentage =
    usage?.usage?.limit > 0 ? (usage.usage.used / usage.usage.limit) * 100 : 0;

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="text-lg font-medium text-foreground">
          {paymentType === "one_time" ? "Access Status" : "Subscription Status"}
        </h3>
        {paymentType === "one_time" && (
          <p className="text-sm text-muted-foreground mt-1">
            {getPaymentTypeDisplay()}
          </p>
        )}
      </div>

      <div className="p-6 space-y-4">
        {/* Tier and Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-sm text-muted-foreground">
              {paymentType === "one_time" ? "Current Access:" : "Current Plan:"}
            </span>
            {/* For one-time payments, show credits if available */}
            {paymentType === "one_time" &&
            subscription?.totalCredits !== undefined ? (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  subscription.totalCredits > 0
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {subscription.totalCredits} Credit
                {subscription.totalCredits !== 1 ? "s" : ""}
              </span>
            ) : (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTierBadgeColor(
                  subscription?.tier || "free",
                )}`}
              >
                {getTierName(subscription?.tier || "free")}
              </span>
            )}
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(
              subscription?.status || "inactive",
            )}`}
          >
            {subscription?.status
              ? subscription.status.charAt(0).toUpperCase() +
                subscription.status.slice(1)
              : "Inactive"}
          </span>
        </div>

        {/* Features */}
        <div className="space-y-2">
          {paymentType === "one_time" &&
          subscription?.totalCredits !== undefined ? (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Credits Purchased:
                </span>
                <span className="font-medium">
                  {subscription?.totalCredits || 0} credit
                  {subscription?.totalCredits !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Domains Used:</span>
                <span className="font-medium">
                  {subscription?.domainsUsed || 0} domain
                  {subscription?.domainsUsed !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Credits Available:
                </span>
                <span
                  className={`font-medium ${
                    subscription?.availableCredits > 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {subscription?.availableCredits || 0} credit
                  {subscription?.availableCredits !== 1 ? "s" : ""}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {paymentType === "one_time"
                    ? "Analysis Items:"
                    : "Action Items:"}
                </span>
                <span className="font-medium">
                  {subscription?.features?.actionItemsLimit === 100
                    ? "All"
                    : subscription?.features?.actionItemsLimit || 0}{" "}
                  items
                </span>
              </div>
              {paymentType === "subscription" && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Monthly Domains:
                  </span>
                  <span className="font-medium">
                    {subscription?.features?.domainsPerMonth || 0} domains
                  </span>
                </div>
              )}
              {paymentType === "one_time" && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    Domain Analysis:
                  </span>
                  <span className="font-medium">
                    {subscription?.features?.domainsPerMonth || 1} domain
                    {subscription?.features?.domainsPerMonth !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Usage Progress */}
        {usage?.usage && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {paymentType === "one_time"
                  ? "Analysis Usage:"
                  : "Domains Used:"}
              </span>
              <span className="font-medium">
                {usage.usage.used} / {usage.usage.limit}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  usagePercentage >= 90
                    ? "bg-red-500"
                    : usagePercentage >= 70
                      ? "bg-yellow-500"
                      : "bg-green-500"
                }`}
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              />
            </div>
            {usage.usage.periodEnd && paymentType === "subscription" && (
              <p className="text-xs text-muted-foreground">
                Resets on {new Date(usage.usage.periodEnd).toLocaleDateString()}
              </p>
            )}
            {paymentType === "one_time" && (
              <p className="text-xs text-muted-foreground">
                Credits don’t expire. Each one covers one domain.
              </p>
            )}
          </div>
        )}

        {/* Payment Management CTA - Hide on upgrade page */}
        {!isOnUpgradePage && (
          <div className="pt-4 border-t border-border">
            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (paymentType === "one_time") {
                    setShowPurchaseModal(true);
                  } else {
                    window.location.href = "/account/upgrade";
                  }
                }}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2 px-4 rounded-md hover:from-blue-700 hover:to-purple-700 transition-colors text-sm font-medium"
              >
                {paymentType === "one_time"
                  ? subscription?.availableCredits === 0
                    ? "Purchase Credits"
                    : "Purchase More Credits"
                  : "Manage Subscription"}
              </button>
              {paymentType === "subscription" && (
                <button
                  onClick={syncWithStripe}
                  disabled={syncing || loading}
                  className="flex items-center justify-center px-3 py-2 border border-border text-card-foreground bg-card rounded-md hover:bg-background focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  title="Sync with Stripe"
                >
                  {syncing ? (
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      className="h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  )}
                </button>
              )}
              {paymentType === "one_time" && (
                <button
                  onClick={syncCredits}
                  disabled={syncingCredits || loading}
                  className="flex items-center justify-center px-3 py-2 border border-border text-card-foreground bg-card rounded-md hover:bg-background focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  title="Sync Credits with Stripe"
                >
                  {syncingCredits ? (
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      className="h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Sync warning - only for subscriptions */}
        {paymentType === "subscription" && subscription?.syncWarning && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-amber-800 font-medium">
                  Sync Issue Detected
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Your subscription is active but we couldn't verify the latest
                  details. Your access is not affected.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Cancellation warning - only for subscriptions */}
        {paymentType === "subscription" && subscription?.cancelAtPeriodEnd && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <p className="text-sm text-yellow-800">
              Your subscription will end on{" "}
              {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>

      <PurchaseCreditsModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
      />
    </div>
  );
};

export default SubscriptionStatus;
